// @ts-check

/**
 * @file Renders the tasks index route.
 */

import {FlashNotifications} from "flash-notifications"
import Text from "@kaspernj/api-maker/build/utils/text"
import TextInput from "../../src/components/text-input"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../src/components/button"
import buildRoutePath from "../../src/shared/build-route-path"
import confirmModal from "../../src/shared/confirm-modal"
import EmptyState from "../../src/components/empty-state"
import errorMessage from "../../src/shared/error-message"
import LoadingState from "../../src/components/loading-state"
import PageHeader from "../../src/components/page-header"
import Pagination from "../../src/components/pagination"
import ProjectsSelect from "../../src/components/projects/select"
import {pushQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import ScreenTitle from "../../src/components/screen-title"
import {Task} from "../../src/frontend-models/task"
import TaskCard from "../../src/components/task-card"
import {identifierListParam} from "../../src/shared/route-params"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @type {Record<string, object>} */
const styles = {}
const PAGE_SIZE = 30
const TASK_CARD_PRELOAD_SELECT = {
  Project: ["id", "name"]
}
const TASK_CARD_SELECT = ["canClose", "createdAt", "id", "name", "priority", "projectId", "state"]
const TASKS_QUERY_PARAM_SELECTOR = ["project_ids"]

/** @typedef {{[key: string]: unknown}} TasksIndexScreenProps */

/**
 * @typedef {object} TasksIndexScreenState
 * @property {boolean} deleting Whether a delete action is in progress.
 * @property {boolean} loading Whether tasks are loading.
 * @property {number} page Current result page.
 * @property {string[]} projectIds Selected project identifiers.
 * @property {string} searchQuery Current search filter text.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown above the list.
 * @property {Task[]} tasks Loaded tasks for the active page.
 * @property {number} totalCount Number of matching tasks.
 */

/**
 * Renders task filters, task cards, and pagination.
 * @augments {ShapeComponent<TasksIndexScreenProps, TasksIndexScreenState>}
 */
class TasksIndexScreen extends ShapeComponent {
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RouteQueryParams} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  loadRequestId = 0
  searchDebounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ null
  /** @type {TasksIndexScreenState} */
  state = {
    deleting: false,
    loading: true,
    page: 1,
    projectIds: /** @type {string[]} */ [],
    searchQuery: "",
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    tasks: /** @type {Task[]} */ [],
    totalCount: 0
  }

  /**
   * Initializes translations, route filters, and loading effects.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(TASKS_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()

    const initialProjectIds = identifierListParam(params.project_ids, "Project IDs")
    this.currentQueryParams = currentQueryParams
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.state.projectIds = initialProjectIds
    useEffect(() => {
      void this.loadTasks()
    }, [this.s.page, this.s.searchQuery, this.s.projectIds.join(",")]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      return () => {
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the tasks index route.
   * @returns {ReactNode} Tasks index content.
   */
  render() {
    const {loading, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="tasksIndexScreen"
      >
        <ScreenTitle>{_("Tasks")}</ScreenTitle>
        <View
          testID="tasksIndexScreen/content"
        >
          <PageHeader
            actions={
              <Button
                href={buildRoutePath(this.tt.params, "/tasks/new")}
                iconName="plus"
                label={_("New task")}
                testID="tasksIndexScreen/newButton"
              />
            }
            subtitle={_("Track and manage tasks across projects")}
            testID="tasksIndexScreen/header"
            title={_("Tasks")}
          />
          <TextInput
            onChangeText={this.tt.onSearchChangeText}
            placeholder={_("Search tasks by name or description...")}
            style={styles.searchInput ||= {borderRadius: 10, marginBottom: 12}}
            testID="tasksIndexScreen/searchInput"
          />
          {this.renderProjectFilter()}
          {status &&
            <Text
              style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                color: status.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="tasksIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading && <LoadingState testID="tasksIndexScreen/loadingText" />}
          {!loading && this.renderTasksList()}
          {this.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders pagination controls.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {loading, page, totalCount} = this.s
    if (loading) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="tasksIndexScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Renders the project filter select.
   * @returns {ReactNode} Project filter controls.
   */
  renderProjectFilter() {
    const {projectIds} = this.s
    return (
      <View
        style={styles.filterRow ||= {
          marginBottom: 16
        }}
        testID="tasksIndexScreen/filterRow"
      >
        <Text
          style={styles[`filterLabel-${this.tt.themeName}`] ||= {
            color: this.tt.colors.textMuted,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.5,
            marginBottom: 6,
            textTransform: "uppercase"
          }}
          testID="tasksIndexScreen/filterLabel"
        >
          {_("Project")}
        </Text>
        <ProjectsSelect
          multiple
          onChangeValue={this.tt.onProjectFilterChange}
          placeholder={_("All projects")}
          testID="tasksIndexScreen/projectFilter"
          values={projectIds.map(String)}
        />
      </View>
    )
  }

  /**
   * Renders grouped task cards.
   * @returns {ReactNode} Task list or empty state.
   */
  renderTasksList() {
    const {tasks} = this.s
    if (tasks.length === 0) {
      return (
        <EmptyState
          message={_("No tasks found")}
          testID="tasksIndexScreen/emptyCard"
        />
      )
    }

    /** @type {Task[]} */
    const openTasks = []
    /** @type {Task[]} */
    const closedTasks = []
    for (const task of tasks) {
      if ((task.state() || "open") === "closed") {
        closedTasks.push(task)
      } else {
        openTasks.push(task)
      }
    }
    return (
      <View testID="tasksIndexScreen/list">
        {openTasks.length > 0 &&
          <View
            style={styles.openSection ||= {
              marginBottom: 24
            }}
            testID="tasksIndexScreen/openSection"
          >
            <View
              style={styles.sectionHeader ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 8,
                marginBottom: 8
              }}
              testID="tasksIndexScreen/openSection/header"
            >
              <Text
                style={styles[`sectionHeaderText-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: "uppercase"
                }}
                testID="tasksIndexScreen/openSection/header/text"
              >
                {_("Open")}
              </Text>
              <View
                style={styles[`sectionHeaderBadge-${this.tt.themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: this.tt.colors.surfaceMuted,
                  borderRadius: 999,
                  height: 20,
                  justifyContent: "center",
                  paddingHorizontal: 8
                }}
                testID="tasksIndexScreen/openSection/header/badge"
              >
                <Text
                  style={styles[`sectionHeaderBadgeText-${this.tt.themeName}`] ||= {
                    color: this.tt.colors.textMuted,
                    fontSize: 11,
                    fontWeight: 700
                  }}
                  testID="tasksIndexScreen/openSection/header/badge/text"
                >
                  {String(openTasks.length)}
                </Text>
              </View>
            </View>
            {openTasks.map((task) =>
              <TaskCard
                key={task.id()}
                onDeletePress={this.tt.onDeletePress}
                params={this.tt.params}
                task={task}
                testIDPrefix="tasksIndexScreen"
              />
            )}
          </View>
        }
        {closedTasks.length > 0 &&
          <View
            style={styles.closedSection ||= {
              marginBottom: 16
            }}
            testID="tasksIndexScreen/closedSection"
          >
            <View
              style={styles.closedSectionHeader ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 8,
                marginBottom: 8
              }}
              testID="tasksIndexScreen/closedSection/header"
            >
              <Text
                style={styles[`sectionHeaderText-${this.tt.themeName}`]}
                testID="tasksIndexScreen/closedSection/header/text"
              >
                {_("Closed")}
              </Text>
              <View
                style={styles[`sectionHeaderBadge-${this.tt.themeName}`]}
                testID="tasksIndexScreen/closedSection/header/badge"
              >
                <Text
                  style={styles[`sectionHeaderBadgeText-${this.tt.themeName}`]}
                  testID="tasksIndexScreen/closedSection/header/badge/text"
                >
                  {String(closedTasks.length)}
                </Text>
              </View>
            </View>
            {closedTasks.map((task) =>
              <TaskCard
                key={task.id()}
                onDeletePress={this.tt.onDeletePress}
                params={this.tt.params}
                task={task}
                testIDPrefix="tasksIndexScreen"
              />
            )}
          </View>
        }
      </View>
    )
  }

  /**
   * Debounces search text updates.
   * @param {string} value Search input value.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.searchDebounceTimer = setTimeout(() => {
      if (value !== this.s.searchQuery) {
        this.s.page = 1
        this.s.searchQuery = value
      }
    }, 300)
  }

  /**
   * Applies selected project filters.
   * @param {(string | number)[] | undefined} values Selected project values.
   * @returns {void} Nothing.
   */
  onProjectFilterChange = (values) => {
    /** @type {string[]} */
    const nextIds = identifierListParam(values, "Project IDs")
    if (sameIds(nextIds, this.s.projectIds)) return
    pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/tasks", {
      project_ids: nextIds.length === 0 ? undefined : nextIds.map(String)
    })
    this.s.page = 1
    this.s.projectIds = nextIds
  }

  /**
   * Applies the selected result page.
   * @param {number} nextPage Page to load.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Deletes a task after confirmation.
   * @param {Task} task Task selected for deletion.
   * @returns {Promise<void>} Resolves after the delete flow finishes.
   */
  onDeletePress = async (task) => {
    const taskId = task.id()
    const taskName = task.name() ?? ""
    const ok = await confirmModal(
      _("Delete task: %{name}?", {
        name: taskName
      }),
      {
        cancelLabel: _("Cancel"),
        confirmLabel: _("Delete"),
        title: _("Confirm delete"),
        tone: "destructive"
      }
    )
    if (!ok) return
    this.s.deleting = true
    this.s.status = null
    try {
      await task.destroy()
      FlashNotifications.success(_("Task deleted"))
      const remaining = this.s.tasks.filter((candidate) => candidate.id() !== taskId)
      this.s.tasks = remaining
      this.s.totalCount = Math.max(0, this.s.totalCount - 1)
      if (remaining.length === 0 && this.s.page > 1) {
        this.s.page -= 1
      }
    } catch (error) {
      const message = errorMessage(error)

      FlashNotifications.error(message)
      this.s.status = {
        kind: "error",
        text: message
      }
    } finally {
      this.s.deleting = false
    }
  }

  /**
   * Loads tasks for the active filters and page.
   * @returns {Promise<void>} Resolves after tasks are loaded.
   */
  loadTasks = async () => {
    const {page, projectIds, searchQuery} = this.s
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      /** @type {Record<string, unknown>} */
      const ransackParams = {
        s: "name asc, id asc"
      }
      if (searchQuery) ransackParams.nameCont = searchQuery
      if (projectIds.length > 0) ransackParams.projectIdIn = projectIds
      const baseQuery = Task.ransack(ransackParams)
      const [tasks, totalCount] = await Promise.all([
        baseQuery
          .clone()
          .preload({project: true})
          .select(TASK_CARD_SELECT)
          .select(TASK_CARD_PRELOAD_SELECT)
          .withCount({
            timelogs: true,
            comments: true,
            assignedUsersCount: {relationship: "taskAssignedUsers"}
          })
          .page(page)
          .perPage(PAGE_SIZE)
          .toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.tasks = tasks
      this.s.totalCount = totalCount
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<TasksIndexScreenProps>} */
const TasksIndexScreenComponent = memo(shapeComponent(TasksIndexScreen))

export default TasksIndexScreenComponent

/**
 * Compares two sorted identifier lists.
 * @param {string[]} a First identifier list.
 * @param {string[]} b Second identifier list.
 * @returns {boolean} True when both lists contain the same ids in order.
 */
function sameIds(a, b) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}
